import React from 'react';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { Visibility as VisibilityIcon, Download as DownloadIcon, ContentCopy as CopyIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import ResponsiveTable from '../../../components/common/ResponsiveTable';
import QuoteService from '../services/quoteService';

const quoteService = QuoteService;

const QuoteTable = ({
  data,
  loading,
  sortField,
  sortDirection,
  onSort,
  onViewQuote,
  onDownloadQuote,
  onCopyLink,
  onEditQuote,
  onDeleteQuote,
  clientLookup = {},
  projectLookup = {},
}) => {
  const columns = [
    {
      field: 'quote_number',
      headerName: 'Αρ. Προσφοράς',
      sortable: true,
      minWidth: 160,
      renderCell: (value) => (
        <Typography variant="body2" fontWeight={600}>
          {value}
        </Typography>
      ),
    },
    {
      field: 'client_id',
      headerName: 'Πελάτης',
      sortable: true,
      minWidth: 220,
      renderCell: (value, row) => {
        // If no client_id, check for custom client name
        if (!value && row.company_info?.full_name) {
          return (
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              {row.company_info.full_name}
            </Typography>
          );
        }
        return (
          <Typography variant="body2">
            {clientLookup?.[value] || (value ? `#${value}` : '—')}
          </Typography>
        );
      },
    },
    {
      field: 'project_id',
      headerName: 'Έργο',
      sortable: false,
      minWidth: 200,
      renderCell: (value) => (
        <Typography variant="body2" color="text.secondary">
          {value ? projectLookup?.[value] || `#${value}` : '-'}
        </Typography>
      ),
    },
    {
      field: 'total_amount',
      headerName: 'Σύνολο',
      sortable: true,
      minWidth: 180,
      renderCell: (value, row) => {
        const subtotal = parseFloat(row.subtotal || 0);
        const vatAmount = parseFloat(row.vat_amount || 0);
        const totalAmount = parseFloat(value || 0);
        
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, gap: 0.3 }}>
            <Typography variant="body2" fontWeight={600}>
              {quoteService.formatCurrency(subtotal)}
            </Typography>
            {vatAmount > 0 && (
              <Typography variant="caption" color="text.secondary">
                ΦΠΑ: {quoteService.formatCurrency(vatAmount)}
              </Typography>
            )}
            <Typography variant="caption" color="success.main" fontWeight={600}>
              Σύνολο: {quoteService.formatCurrency(totalAmount)}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Κατάσταση',
      sortable: true,
      minWidth: 140,
      renderCell: (value) => (
        <Chip
          label={quoteService.formatStatus(value)}
          size="small"
          color={quoteService.getStatusColor(value)}
        />
      ),
    },
    {
      field: 'valid_until',
      headerName: 'Ισχύει έως',
      sortable: true,
      minWidth: 140,
      renderCell: (value) => (
        <Typography variant="body2">
          {value ? new Date(value).toLocaleDateString('el-GR') : '-'}
        </Typography>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Δημιουργήθηκε',
      sortable: true,
      minWidth: 160,
      renderCell: (value) => (
        <Typography variant="body2" color="text.secondary">
          {value ? new Date(value).toLocaleString('el-GR') : '-'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Ενέργειες',
      sortable: false,
      minWidth: 200,
      renderCell: (value, row) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Προβολή">
            <IconButton size="small" color="primary" onClick={() => onViewQuote(row)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Επεξεργασία">
            <IconButton size="small" onClick={() => onEditQuote && onEditQuote(row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Λήψη PDF">
            <IconButton size="small" color="secondary" onClick={() => onDownloadQuote(row)}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Αντιγραφή link">
            <IconButton size="small" color="info" onClick={() => onCopyLink(row)}>
              <CopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Διαγραφή">
            <IconButton size="small" color="error" onClick={() => onDeleteQuote && onDeleteQuote(row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <ResponsiveTable
      data={data}
      columns={columns}
      loading={loading}
      emptyMessage="Δεν υπάρχουν προσφορές"
      sortField={sortField}
      sortDirection={sortDirection}
      onSort={onSort}
      renderMobileCard={(quote) => (
        <Box key={quote.id} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" fontWeight={600}>
              {quote.quote_number}
            </Typography>
            <Chip
              label={quoteService.formatStatus(quote.status)}
              size="small"
              color={quoteService.getStatusColor(quote.status)}
            />
          </Box>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Πελάτης: {quote.client_id 
              ? (clientLookup?.[quote.client_id] || `#${quote.client_id}`)
              : (quote.company_info?.full_name || '—')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Έργο: {quote.project_id ? projectLookup?.[quote.project_id] || `#${quote.project_id}` : '-'}
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.3 }}>
            <Typography variant="body2" fontWeight={600}>
              {quoteService.formatCurrency(quote.subtotal || 0)}
            </Typography>
            {quote.vat_amount > 0 && (
              <Typography variant="caption" color="text.secondary">
                ΦΠΑ: {quoteService.formatCurrency(quote.vat_amount || 0)}
              </Typography>
            )}
            <Typography variant="caption" color="success.main" fontWeight={600}>
              Σύνολο: {quoteService.formatCurrency(quote.total_amount || 0)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <IconButton size="small" color="primary" onClick={() => onViewQuote(quote)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
            {onEditQuote && (
              <IconButton size="small" onClick={() => onEditQuote(quote)}>
                <EditIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton size="small" color="secondary" onClick={() => onDownloadQuote(quote)}>
              <DownloadIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" color="info" onClick={() => onCopyLink(quote)}>
              <CopyIcon fontSize="small" />
            </IconButton>
            {onDeleteQuote && (
              <IconButton size="small" color="error" onClick={() => onDeleteQuote(quote)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>
      )}
    />
  );
};

export default QuoteTable;

